import { PlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useMemo, useState } from 'react';

import useSWR from 'swr';

import { Button } from '../../../components/button/Button';
import { Combobox } from '../../../components/combobox/Combobox';
import { InputWrapper } from '../../../components/InputWrapper';
import { Country } from 'src/types/enums';
import { fetchCustomerPortalLocations } from './models';
import { LocationPreviewDialog } from './CustomerPortalLocationPreviewDialog';
import { CustomerPortalCreateLocationDialog } from './CustomerPortalCreateLocationDialog';
import { useTranslation } from '../../../contexts/translation-context';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { getDisplayError } from '../../../utils/get-display-error';

export interface IMinimalLocation {
  id: number;
  name: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: Country;
}

type ComboValue = IMinimalLocation;

const displayLocation = (location: ComboValue) => {
  return `${location.name} - ${location.city} ${location.country}`;
};

export interface ILocationComboboxProps {
  value: ComboValue | null;
  onChange: (val: IMinimalLocation | null) => void;
  onBlur?: () => void;
  invalidText?: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
  hideInvalid?: boolean;
  isNullable?: boolean;
}

export const CustomerPortalLocationCombobox: React.FC<ILocationComboboxProps> = (props) => {
  const {
    value,
    onChange,
    onBlur,
    invalidText,
    labelText,
    helperText,
    isDisabled,
    isNullable = true,
    hideInvalid,
  } = props;
  const { i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [showLocationPreview, setShowLocationPreview] = useState(false);
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  const { customer } = useCustomerPortalCustomer();
  const { data, error } = useSWR(['customer-portal-locations', customer.id, searchValue], () =>
    fetchCustomerPortalLocations({ customerId: customer.id, search: searchValue }),
  );

  const locations: ComboValue[] = useMemo(() => {
    return data ? [...data] : [];
  }, [data]);

  return (
    <div className="flex gap-2">
      <InputWrapper
        labelText={labelText ?? i18n('customerPortal.locations.location')}
        invalidText={invalidText}
        helperText={helperText}
        hideInvalid={hideInvalid}
      >
        <Combobox
          keyName="id"
          placeholder={i18n('customerPortal.locations.findLocation')}
          onQueryChange={setSearchValue}
          display={displayLocation}
          items={locations}
          selectedItem={value}
          onSelect={(val) => {
            onChange(val);
            setShowLocationPreview(false);
          }}
          onBlur={onBlur}
          isDisabled={isDisabled}
          isInvalid={!!invalidText}
          isNullable={isNullable}
        />
        {error ? <div className="text-sm text-negative-text mt-1">{getDisplayError(error)}</div> : null}
      </InputWrapper>

      {value && (
        <div className="flex items-center">
          <Button
            color="secondary"
            onTrigger={() => {
              setShowLocationPreview(true);
            }}
          >
            <MagnifyingGlassIcon className="button-icon" />
          </Button>
          <LocationPreviewDialog location={value} isOpen={showLocationPreview} setIsOpen={setShowLocationPreview} />
        </div>
      )}

      <div className="flex items-center">
        <Button
          color="secondary"
          onTrigger={() => {
            setShowCreationDialog(true);
          }}
        >
          <PlusIcon className="button-icon" />
        </Button>
        <CustomerPortalCreateLocationDialog
          isOpen={showCreationDialog}
          setIsOpen={setShowCreationDialog}
          onCreated={(data) => {
            onChange(data);
            setShowCreationDialog(false);
            setShowLocationPreview(false);
          }}
        />
      </div>
    </div>
  );
};

export interface ICustomerPortalLocationComboboxFieldProps {
  name: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
}

export const CustomerPortalLocationComboboxField: React.FC<ICustomerPortalLocationComboboxFieldProps> = (props) => {
  const { name, labelText, helperText, isDisabled } = props;
  const [field, meta, helpers] = useField({ name });

  return (
    <CustomerPortalLocationCombobox
      value={field.value}
      onChange={(val) => helpers.setValue(val)}
      onBlur={() => helpers.setTouched(true)}
      invalidText={(meta.touched && meta.error) || undefined}
      labelText={labelText}
      helperText={helperText}
      isDisabled={isDisabled}
    />
  );
};
